import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react'
import { useHistory, withRouter } from 'react-router-dom'
import SmallLogo from '../../images/pejlen-icon.svg'
import Logo from '../../images/pejlen_liggande.svg'

// Antd
import { BarChartOutlined, LineChartOutlined, LogoutOutlined, MenuFoldOutlined, MenuUnfoldOutlined, SettingOutlined, TeamOutlined, WarningOutlined } from '@ant-design/icons'
import type { MenuProps } from 'antd'
import { Button, Layout, Menu } from 'antd'
import cryptoRandomString from 'crypto-random-string'
import { useDispatch, useSelector } from 'react-redux'
import { useApi } from '../../api/useApi'
import { setAnsvarigPerson } from '../../store/ansvarigPersonSlice'
import { getMe, setMe, setOrganization } from '../../store/organizationSlice'
import { getActiveMenuItem, setActiveMenuItem } from '../../store/sideMenuSlice'
import siderMenuPermissions from '../../utils/siderMenuPermissions'
import { PermissionTypes } from './utils/generateSideMenuChildren'
import { siderMenuItem } from './utils/siderMenuItem'
import { useMunicipalityName } from '../../api/benchmarking'
const { Sider } = Layout

const hydra_url = process.env.REACT_APP_HYDRA_URL
const api_url = process.env.REACT_APP_API_URL

export type MenuItem = Required<MenuProps>['items'][number]

const shouldDefaultClosed = (pathname: string) => {
  // Benchmarking starts collapsed
  if (pathname.includes('/app/benchmarking') && !pathname.includes('/app/benchmarking/introduction')) {
    return true
  }
  if (pathname.includes('/app/hemtjanst')) {
    return true
  }
  return false
}

const siderMenu = withRouter(({ history }) => {
  const { data: municipalityName } = useMunicipalityName()
  const navigate = useHistory()
  const api = useApi()
  const me = useSelector(getMe)
  const [collapsed, setCollapsed] = useState(shouldDefaultClosed(navigate.location.pathname))

  // Stegad collapse-koreografi (samma stil för hela menyn):
  // 1) innehållet tonar ut snabbt → 2) bredden animerar (lite längre) medan
  // ytan är tom → 3) collapsed-innehållet (liten logga + ikoner) easas in efter
  // att bredden lagt sig. Att layout-swappen sker vid opacity 0 döljer antd:s
  // inbyggda ikon-jank (vertikalt hopp + horisontell drift under animationen).
  const FADE_OUT_MS = 50
  const WIDTH_MS = 200
  const FADE_IN_MS = 200
  const [contentVisible, setContentVisible] = useState(true)
  const fadeOutTimer = useRef<ReturnType<typeof setTimeout>>()

  // Steg 3 (easa in innehållet igen) är bundet till `collapsed` snarare än en
  // lös timer-kedja: effekten kör ALLTID för det slutgiltiga läget, så
  // innehållet kan aldrig fastna osynligt även vid snabb upprepad toggling.
  useEffect(() => {
    const t = setTimeout(() => setContentVisible(true), WIDTH_MS)
    return () => clearTimeout(t)
  }, [collapsed])

  const runStagedCollapse = (target: boolean) => {
    if (target === collapsed) return
    clearTimeout(fadeOutTimer.current)
    setContentVisible(false) // 1. tona ut snabbt
    // 2. animera bredden medan ytan är tom; steg 3 sköts av effekten ovan
    fadeOutTimer.current = setTimeout(() => setCollapsed(target), FADE_OUT_MS)
  }

  const toggleCollapse = () => runStagedCollapse(!collapsed)

  const contentStyle: CSSProperties = {
    opacity: contentVisible ? 1 : 0,
    transition: `opacity ${contentVisible ? FADE_IN_MS : FADE_OUT_MS}ms ease`,
    pointerEvents: contentVisible ? 'auto' : 'none',
  }

  const selectedMenuItem = useSelector(getActiveMenuItem)

  const items: MenuItem[] = [
    siderMenuPermissions(me, 'flags')
      ? ({
          label: 'Flaggor',
          icon: <WarningOutlined />,
          children: [
            siderMenuItem([PermissionTypes.forebyggande_insatser], me.permissions, '/app/forebyggande-insatser', 'Förebyggande insatser'),
            siderMenuItem([PermissionTypes.utförare], me.permissions, '/app/utforare', 'Utförare'),
            siderMenuItem([PermissionTypes.personal], me.permissions, '/app/medarbetare', 'Personal'),
            siderMenuItem([PermissionTypes.brukare], me.permissions, '/app/brukare', 'Brukare'),
            siderMenuItem([PermissionTypes.uppföljningslista], me.permissions, '/app/arenden', 'Hanterade flaggor'),
          ],
        } as MenuItem)
      : null,
    siderMenuPermissions(me, 'uppföljning_statistik')
      ? ({
          label: 'Uppföljning/statistik',
          icon: <BarChartOutlined />,
          children: [siderMenuItem([PermissionTypes.uppföljning_statistik], me.permissions, '/app/hemtjanst', municipalityName)],
        } as MenuItem)
      : null,
    siderMenuPermissions(me, 'benchmarking')
      ? ({
          label: 'Benchmarking',
          icon: <LineChartOutlined />,
          children: [
            siderMenuItem([PermissionTypes.benchmarking], me.permissions, '/app/benchmarking/introduction', 'Introduktion'),
            siderMenuItem([PermissionTypes.benchmarking], me.permissions, '/app/benchmarking', 'Volym'),
            siderMenuItem([PermissionTypes.benchmarking], me.permissions, '/app/benchmarking/berakningsstod', 'Beräkningsstöd'),
          ],
        } as MenuItem)
      : null,
    siderMenuPermissions(me, 'handläggning', ['view_beslutsstöd'])
      ? ({
          label: 'Handläggning',
          icon: <TeamOutlined />,
          children: [
            siderMenuItem([PermissionTypes.ärendefördelning, PermissionTypes.utfanför_teamstruktur], me.permissions, 'översikt-parent', 'Översikt', undefined, [
              siderMenuItem([PermissionTypes.ärendefördelning], me.permissions, '/app/arendefordelning', 'Ärendefördelning'),
              siderMenuItem([PermissionTypes.utfanför_teamstruktur], me.permissions, '/app/utanfor-teamstruktur', 'Utanför struktur'),
            ]),
            siderMenuItem([PermissionTypes.mina_ärenden, PermissionTypes.volymuppföljning, PermissionTypes.nyligen_utgångna], me.permissions, 'handläggning-parent', 'Handläggare', undefined, [
              siderMenuItem([PermissionTypes.mina_ärenden], me.permissions, '/app/mina-arenden', 'Mina ärenden'),
              siderMenuItem([PermissionTypes.volymuppföljning], me.permissions, '/app/volymuppfoljning', 'Volymuppföljning'),
              siderMenuItem([PermissionTypes.nyligen_utgångna], me.permissions, '/app/nyligen-utganga', 'Nyligen utgångna'),
            ]),
            /* Hemtjänstverktyget heter Beräkningsstöd, och vyerna sa redan det – rubriken
               på både huvudvyn och IBIC lyder "Beräkningsstöd för tid i hemtjänsten" –
               medan menyn låg kvar på det gamla ordet.

               Funk är INTE med i bytet. Funktionsstöd är ett eget verktyg som fortsatt
               heter Beslutsstöd, och dess rubrik säger samma sak. De två namnen står
               alltså bredvid varandra med flit.

               Rutter, behörigheter och mappnamn heter fortfarande beslutsstod rakt
               igenom och rörs inte: det är inget användaren ser, och en omdöpning där
               drar med sig databasrader och genererad klient för noll synlig vinst. */
            siderMenuPermissions(me, 'beslutsstöd') && siderMenuItem([PermissionTypes.view_beslutsstöd], me.permissions, '/app/beslutsstod', 'Beräkningsstöd'),
            siderMenuPermissions(me, 'ibic_beslutsstöd') && siderMenuItem([PermissionTypes.view_ibic_beslutsstöd], me.permissions, '/app/ibic-beslutsstod', 'IBIC Beräkningsstöd'),
            siderMenuPermissions(me, 'lss_beslutsstöd') && siderMenuItem([PermissionTypes.view_lss_beslutsstöd], me.permissions, '/app/beslutsstod-lss', 'Beslutsstöd Funk'),
            siderMenuPermissions(me, 'målindex') && siderMenuItem([PermissionTypes.view_målindex], me.permissions, '/app/malindex', 'Målindex'),
          ],
        } as MenuItem)
      : null,
    siderMenuPermissions(me, 'användare')
      ? ({
          label: 'Inställningar',
          icon: <SettingOutlined />,
          children: [
            siderMenuItem([PermissionTypes.användarhantering], me.permissions, '/app/anvandare', 'Användare'),
            siderMenuItem([PermissionTypes.administration], me.permissions, '/app/taggar', 'Taggar'),
            siderMenuItem([PermissionTypes.administration], me.permissions, '/app/gäster', 'Gäster'),
            siderMenuItem([PermissionTypes.administration], me.permissions, '/app/roller', 'Roller'),
            siderMenuItem([PermissionTypes.administration], me.permissions, '/app/övrigt', 'Övrigt'),
            siderMenuItem([PermissionTypes.flaggenerering], me.permissions, '/app/flaggkorning', 'Flaggkörning'),
          ],
        } as MenuItem)
      : null,
  ]

  const handleClick = () => {
    const state = cryptoRandomString({ length: 32 })
    localStorage.setItem('state', state)
    const postLogoutRedirectUri = (() => {
      if (process.env.NODE_ENV === 'development') {
        return 'http://127.0.0.1:5001/logout/dev'
      }
      return `https://pejlen.inrikta.se/logout/${localStorage.getItem('tenant')}`
    })()

    window.location.href = `${hydra_url}/oauth2/sessions/logout?id_token_hint=${localStorage.getItem('hydra_id_token')}&state=${state}&post_logout_redirect_uri=${postLogoutRedirectUri}`
  }

  const dispatch = useDispatch()

  const getOrganization = useCallback(async () => {
    try {
      const data = await api.get(`/v1/handlaggare/${localStorage.getItem('tenant_id')}/structure`, {
        headers: {
          authorization: 'Bearer ' + localStorage.getItem('hydra_access_token'),
        },
      })

      dispatch(setOrganization(data.data))
    } catch (e) {
      console.error(e)
    }
  }, [])

  const getMyself = useCallback(async () => {
    try {
      const data = await api.get(`/v1/handlaggare/${localStorage.getItem('tenant_id')}/me?id_token=${localStorage.getItem('hydra_id_token')}`, {
        headers: {
          authorization: 'Bearer ' + localStorage.getItem('hydra_access_token'),
        },
      })

      dispatch(setMe(data.data))
      dispatch(setAnsvarigPerson({ ansvarigPersonId: data.data.personnummer, ansvarigPersonName: data.data.namn }))
    } catch (e) {
      console.error(e)
    }
  }, [])

  useEffect(() => {
    getOrganization()
    getMyself()
  }, [])

  useEffect(() => {
    // Auto-collapse på vissa vyer (t.ex. Benchmarking > Volym) ska använda samma
    // stegade koreografi som manuell toggle/breakpoint — inte ett rått setCollapsed
    // som animerar bredden med innehållet synligt (= gamla janken).
    if (shouldDefaultClosed(navigate.location.pathname)) {
      runStagedCollapse(true)
    }
  }, [navigate.location.pathname])

  useEffect(() => () => clearTimeout(fadeOutTimer.current), [])

  return (
    <Sider
      theme="light"
      breakpoint={shouldDefaultClosed(navigate.location.pathname) ? undefined : 'lg'}
      collapsedWidth="60px"
      collapsible
      collapsed={collapsed}
      trigger={null}
      onCollapse={(col) => {
        // Hedra bara responsiv KOLLAPS (smal skärm). Ignorera expand: när breakpoint
        // växlar undefined→'lg' (t.ex. när man lämnar Volym/Hemtjänst) fyrar antd ett
        // onCollapse(false) på bred skärm → annars expanderar menyn utan klick.
        if (col) runStagedCollapse(true)
      }}
      style={{
        overflowX: 'hidden',
        overflowY: 'auto',
        height: '100vh',
        position: 'sticky',
        top: 0,
        left: 0,
        transition: `all ${WIDTH_MS}ms ease`,
      }}
    >
      <div style={contentStyle}>
        {collapsed ? (
          <div className="small-logo">
            <img src={SmallLogo} width="38" height="38" />
          </div>
        ) : (
          <div className="logo">
            <img src={Logo} alt="Inrikta logga" width={150} />
          </div>
        )}
        <Menu
          mode="inline"
          selectedKeys={[selectedMenuItem]}
          defaultSelectedKeys={[window.location.pathname]}
          onClick={(a) => {
            dispatch(setActiveMenuItem(a.key))
            history.push(`${a.key}`)
          }}
          items={items}
        />
      </div>
      <div className="sider-bottom" style={contentStyle}>
        <div className="menu-button-wrapper" style={collapsed ? { margin: '0 0 15px 0', display: 'flex', justifyContent: 'center' } : undefined}>
          {collapsed ? (
            <LogoutOutlined style={{ fontSize: 18 }} onClick={handleClick} />
          ) : (
            <Button className="button-no-hover" style={{ paddingLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 10, color: '#000' }} type="text" onClick={handleClick}>
              <LogoutOutlined style={{ fontSize: 16 }} />
              Logga ut
            </Button>
          )}
        </div>
        <div
          className="collapse-trigger"
          onClick={toggleCollapse}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-end',
            gap: 8,
            borderTop: '1px solid #f0f0f0',
            padding: '12px 16px',
            cursor: 'pointer',
            color: 'rgba(0, 0, 0, 0.85)',
            fontSize: collapsed ? 18 : 16,
            boxSizing: 'border-box',
          }}
        >
          {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        </div>
      </div>
    </Sider>
  )
})

export default siderMenu
